import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { matchPath, useLocation } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { deleteCookie, inIframe, useEffectOnce, usePathParam } from '@chilecompra/react-kit'
import { Button, Toast } from '@chilecompra/react-kit/components'
import {
  REACT_APP_BASE_URL,
  REACT_APP_FEATURE_FLAG_CONTRACTUAL_BEHAVIOR_PRIVATE_TAB,
  REACT_APP_FEATURE_FLAG_TEMPORARY_UNIONS,
  REACT_APP_MERCADO_PUBLICO_BASE_URL,
  REACT_APP_FEATURE_FLAG_GENERAL_INFORMATION_TAB,
  REACT_APP_FEATURE_FLAG_ABILITY_TAB,
  REACT_APP_FEATURE_FLAG_CONTRACTUAL_BEHAVIOR_TAB,
  REACT_APP_FEATURE_FLAG_TEMPORARY_UNIONS_TAB,
  REACT_APP_FEATURE_FLAG_ABILITY,
  REACT_APP_FEATURE_FLAG_GENERAL_INFORMATION,
  REACT_APP_FEATURE_FLAG_DOCUMENTS_FRAMEWORK,
  REACT_APP_FEATURE_FLAG_LEGAL_DOCUMENTS,
  REACT_APP_FEATURE_FLAG_TAX_ANNOTATIONS,
  REACT_APP_FEATURE_FLAG_SUITABILITY_SECTION,
  REACT_APP_FEATURE_FLAG_MESSAGE_INFO,
  REACT_APP_FEATURE_FLAG_WORK_REGISTER
} from '../config/settings/environment'
import { PATH_PARAM } from '../config/settings/regexes'
import routes from '../config/settings/routes'
import AuthorizationContext from '../containers/AuthorizationProvider/AuthorizationProvider.context'
import AuthorizationProviderContainer from '../containers/AuthorizationProvider/AuthorizationProvider.container'
import AbilityStatusSection from '../containers/AbilityStatusSection/AbilityStatusSection.container'
import BasicSellerInformationSectionContainer from '../containers/BasicSellerInformationSection/BasicSellerInformationSection.container'
import CompanyAdministrationSection from '../containers/CompanyAdministrationSection/CompanyAdministrationSection.container'
import CompanyContactSection from '../containers/CompanyContactSection/CompanyContactSection.container'
import ContractualBehaviorSection from '../containers/ContractualBehavior/ContractualBehavior.container'
import FrameworksAgreementSectionContainer from '../containers/FrameworkAgreementSection/FrameworksAgreementSection.container'
import EnrollmentCertificateButton from '../containers/EnrollmentCertificateSection/EnrollmentCertificateButton.container'
import FinancialSuitabilitySection from '../containers/FinancialSuitabilitySection/FinancialSuitabilitySection.container'
import HeaderContainer from '../containers/Header/Header.container'
import HeadingSectionContainer from '../containers/HeadingSection/HeadingSection.container'
import LegalDocumentationSection from '../containers/LegalDocumentation/LegalDocumentationSection.container'
import LegalSuitabilitySection from '../containers/LegalSuitabilitySection/LegalSuitabilitySection.container'
import TaxAnnotationsSection from '../containers/TaxAnnotationsSection/TaxAnnotationsSection.container'
import NoAccreditationContainer from '../containers/NoAccreditationSection/NoAccreditationSection.container'
import PartnershipGridSection from '../containers/PartnershipGridSection/PartnershipGridSection.container'
import PrivateSection from '../components/Security/PrivateSection'
import PublicSection from '../components/Security/PublicSection'
import SellerTabWrapper from '../containers/SellerTabWrapper/SellerTabWrapper.container'
import SiiInformationSection from '../containers/SiiInformationSection/SiiInformationSection.container'
import StampSectionContainer from '../containers/StampsSection/StampsSection.container'
import SummarySectionContainer from '../containers/SummarySection/SummarySection.container'
import TechnicalSuitabilitySection from '../containers/TechnicalSuitabilitySection/TechnicalSuitabilitySection.container'
import BrandLayout from '../components/Layouts/BrandLayout'
import ProtectByFeatures from '../components/Security/ProtectByFeatures'
import { Row } from './SellerCard.styles'
import { Link } from '../containers/PartnershipGridSection/PartnershipGridSection.styles'
import { TemporaryUnionsSection } from '../containers/TemporaryUnionsSection/TemporaryUnionsSection.container'
import UniqueSellersSection from '../containers/UniqueSellersSection/UniqueSellersSection.container'
import PurchaseOrderDialog from '../containers/PurchaseOrderDialog/PurchaseOrderDialog.container'
import { USER_TYPE } from '../config/settings/constant'
import useReCaptcha from '../modules/hooks/reCaptcha'
import FinalBeneficiarySection from '../containers/FinalBeneficiarySection/FinalBeneficiarySection.container'
import RelatedCompanySection from '../containers/RelatedCompanySection/RelatedCompanySection.container'
import FilePDFButton from '../containers/EnrollmentCertificateSection/FilePDFButton.container'
import RedirectAffidavitButton from '../containers/EnrollmentCertificateSection/RedirectAffidavitButton.container'
import { checkIfForeignDni } from '../modules/utils/helpers'
import { WorkRegister } from '../containers/WorkRegister/WorkRegister.container'

/**
 * The SellerCard' page.
 */
const SellerCard = () => {
  const {
    authInfo: { userType }
  } = useSelector(state => state.authorizationProvider)
  const { contractStatus } = useSelector(state => state.basicSellerInformation)
  const { initializeReCaptcha, executeReCaptcha } = useReCaptcha()
  const { pathname } = useLocation()
  const dni = usePathParam('rut')
  const [token, setToken] = useState()

  const isForeign = checkIfForeignDni(dni)

  const breadcrumbs = useCallback(
    (isPrivated, userType) => {
      return [
        {
          text: !isPrivated ? 'Home' : 'Escritorio',
          link: !isPrivated
            ? `${REACT_APP_MERCADO_PUBLICO_BASE_URL}/Home`
            : `${REACT_APP_MERCADO_PUBLICO_BASE_URL}/Portal/Modules/Menu/Menu.aspx`,
          isExternal: true,
          onClick: event => handleClickHome(event, isPrivated, userType)
        },
        userType !== 'Proveedor' && {
          text: 'Búsqueda Proveedor',
          link: `${REACT_APP_BASE_URL}/busqueda`,
          isExternal: true
        },
        { text: dni }
      ].filter(Boolean)
    },
    [dni]
  )

  const title = 'Ficha del proveedor'

  const optionTabs = [
    (REACT_APP_FEATURE_FLAG_GENERAL_INFORMATION === true || REACT_APP_FEATURE_FLAG_GENERAL_INFORMATION === 'true') && {
      label: 'Información general',
      content: (
        <>
          <ProtectByFeatures rule={features => features['com.seller/private.unique.seller']}>
            <UniqueSellersSection />
          </ProtectByFeatures>
          <SiiInformationSection />
          <CompanyAdministrationSection />
          <CompanyContactSection />
          <ProtectByFeatures rule={features => features['com.seller/stamp']}>
            <StampSectionContainer />
          </ProtectByFeatures>
          <ProtectByFeatures rule={features => features['com.seller/partnership.section']}>
            <PartnershipGridSection />
          </ProtectByFeatures>
          <ProtectByFeatures rule={features => features['com.seller/final.beneficiary.section']}>
            <FinalBeneficiarySection />
          </ProtectByFeatures>
          <ProtectByFeatures rule={features => features['com.seller/related.company.section']}>
            <RelatedCompanySection />
          </ProtectByFeatures>
        </>
      ),
      current: !!matchPath(routes.sellerCard, pathname) || !!matchPath(routes.generalInformation, pathname),
      route: routes.generalInformation.replace(PATH_PARAM, dni),
      show: () => true
    },
    (REACT_APP_FEATURE_FLAG_ABILITY === true || REACT_APP_FEATURE_FLAG_ABILITY === 'true') && {
      label: 'Habilidad',
      content: <AbilityStatusSection />,
      current: !!matchPath(routes.ability, pathname),
      route: routes.ability.replace(PATH_PARAM, dni),
      show: ({ summaryError }) => summaryError?.reason !== 'FORBIDDEN_ERROR'
    },
    {
      label: 'Idoneidad',
      content: (
        <>
          <LegalSuitabilitySection />
          <FinancialSuitabilitySection />
          <ProtectByFeatures rule={features => features['com.seller/tax.annotations']}>
            <TaxAnnotationsSection />
          </ProtectByFeatures>
          <TechnicalSuitabilitySection />
        </>
      ),
      current: !!matchPath(routes.suitability, pathname),
      route: routes.suitability.replace(PATH_PARAM, dni),
      show: ({ summaryError }) => {
        return (
          summaryError?.reason !== 'FORBIDDEN_ERROR' &&
          (REACT_APP_FEATURE_FLAG_TAX_ANNOTATIONS === true ||
            REACT_APP_FEATURE_FLAG_TAX_ANNOTATIONS === 'true' ||
            REACT_APP_FEATURE_FLAG_SUITABILITY_SECTION === true ||
            REACT_APP_FEATURE_FLAG_SUITABILITY_SECTION === 'true')
        )
      }
    },
    {
      label: 'Documentos',
      content: (
        <>
          <NoAccreditationContainer />
          <ProtectByFeatures rule={features => features['com.seller/legal.documents']}>
            <LegalDocumentationSection />
          </ProtectByFeatures>
          <ProtectByFeatures rule={features => features['com.seller/documents.framework']}>
            <FrameworksAgreementSectionContainer />
          </ProtectByFeatures>
        </>
      ),
      current: !!matchPath(routes.documents, pathname),
      route: routes.documents.replace(PATH_PARAM, dni),
      show: ({ summaryError }) => {
        return (
          summaryError?.reason !== 'FORBIDDEN_ERROR' &&
          (REACT_APP_FEATURE_FLAG_DOCUMENTS_FRAMEWORK === true ||
            REACT_APP_FEATURE_FLAG_DOCUMENTS_FRAMEWORK === 'true' ||
            REACT_APP_FEATURE_FLAG_LEGAL_DOCUMENTS === true ||
            REACT_APP_FEATURE_FLAG_LEGAL_DOCUMENTS === 'true')
        )
      }
    },
    (REACT_APP_FEATURE_FLAG_CONTRACTUAL_BEHAVIOR_PRIVATE_TAB === true ||
      REACT_APP_FEATURE_FLAG_CONTRACTUAL_BEHAVIOR_PRIVATE_TAB === 'true') && {
      label: 'Comportamiento',
      content: <ContractualBehaviorSection />,
      current: !!matchPath(routes.contractualBehavior, pathname),
      route: routes.contractualBehavior.replace(PATH_PARAM, dni),
      show: () => {
        return (
          REACT_APP_FEATURE_FLAG_CONTRACTUAL_BEHAVIOR_PRIVATE_TAB === true ||
          REACT_APP_FEATURE_FLAG_CONTRACTUAL_BEHAVIOR_PRIVATE_TAB === 'true'
        )
      }
    },
    (REACT_APP_FEATURE_FLAG_TEMPORARY_UNIONS === true || REACT_APP_FEATURE_FLAG_TEMPORARY_UNIONS === 'true') && {
      label: 'Uniones Temporales',
      content: (
        <ProtectByFeatures rule={features => features['com.seller/temporary.unions']}>
          <TemporaryUnionsSection />
        </ProtectByFeatures>
      ),
      current: !!matchPath(routes.temporaryUnions, pathname) || false,
      route: routes.temporaryUnions.replace(PATH_PARAM, dni),
      show: ({ summaryError }) => summaryError?.reason !== 'FORBIDDEN_ERROR'
    },
    (REACT_APP_FEATURE_FLAG_WORK_REGISTER === true || REACT_APP_FEATURE_FLAG_WORK_REGISTER === 'true') && {
      label: 'Registro Obras',
      content: (
        <ProtectByFeatures rule={features => features['com.seller/work.register.visible']}>
          <WorkRegister />
        </ProtectByFeatures>
      ),
      current: !!matchPath(routes.workRegister, pathname) || false,
      route: routes.workRegister.replace(PATH_PARAM, dni),
      show: ({ summaryError }) => summaryError?.reason !== 'FORBIDDEN_ERROR'
    }
  ].filter(Boolean)

  const optionTabsPublic = [
    {
      label: 'Información general',
      content: (
        <ProtectByFeatures rule={features => features['com.seller/tab.general.information']}>
          <ProtectByFeatures rule={features => features['com.seller/public.unique.seller']}>
            <UniqueSellersSection />
          </ProtectByFeatures>
          <CompanyAdministrationSection />
          <ProtectByFeatures rule={features => features['com.seller/stamp']}>
            <StampSectionContainer />
          </ProtectByFeatures>
          <ProtectByFeatures rule={features => features['com.seller/partnership.section']}>
            <PartnershipGridSection />
          </ProtectByFeatures>
          <ProtectByFeatures rule={features => features['com.seller/final.beneficiary.section']}>
            <FinalBeneficiarySection />
          </ProtectByFeatures>
          <ProtectByFeatures rule={features => features['com.seller/related.company.section']}>
            <RelatedCompanySection />
          </ProtectByFeatures>
        </ProtectByFeatures>
      ),
      current: !!matchPath(routes.sellerCard, pathname) || !!matchPath(routes.generalInformation, pathname),
      route: routes.generalInformation.replace(PATH_PARAM, dni),
      show: () => {
        return (
          REACT_APP_FEATURE_FLAG_GENERAL_INFORMATION_TAB === true ||
          REACT_APP_FEATURE_FLAG_GENERAL_INFORMATION_TAB === 'true'
        )
      }
    },
    {
      label: 'Habilidad',
      content: (
        <ProtectByFeatures rule={features => features['com.seller/tab.ability']}>
          <AbilityStatusSection />
        </ProtectByFeatures>
      ),
      current: !!matchPath(routes.ability, pathname),
      route: routes.ability.replace(PATH_PARAM, dni),
      show: () => {
        return (
          (REACT_APP_FEATURE_FLAG_ABILITY_TAB === true || REACT_APP_FEATURE_FLAG_ABILITY_TAB === 'true') &&
          (contractStatus !== 0 || isForeign)
        )
      }
    },
    {
      label: 'Comportamiento',
      content: (
        <ProtectByFeatures rule={features => features['com.seller/tab.contractual.behavior']}>
          <ContractualBehaviorSection />
        </ProtectByFeatures>
      ),
      current: !!matchPath(routes.contractualBehavior, pathname),
      route: routes.contractualBehavior.replace(PATH_PARAM, dni),
      show: () => {
        return (
          REACT_APP_FEATURE_FLAG_CONTRACTUAL_BEHAVIOR_TAB === true ||
          REACT_APP_FEATURE_FLAG_CONTRACTUAL_BEHAVIOR_TAB === 'true'
        )
      }
    },
    {
      label: 'Uniones Temporales',
      content: (
        <ProtectByFeatures rule={features => features['com.seller/tab.temporary.unions']}>
          <TemporaryUnionsSection />
        </ProtectByFeatures>
      ),
      current: !!matchPath(routes.temporaryUnions, pathname) || false,
      route: routes.temporaryUnions.replace(PATH_PARAM, dni),
      show: () => {
        return (
          REACT_APP_FEATURE_FLAG_TEMPORARY_UNIONS_TAB === true || REACT_APP_FEATURE_FLAG_TEMPORARY_UNIONS_TAB === 'true'
        )
      }
    }
  ].filter(Boolean)

  const handleClickHome = (event, isPrivated, userType) => {
    event.preventDefault()
    deleteCookie('access_token')
    if (isPrivated && userType === USER_TYPE.PROVIDER) {
      window.open(`${REACT_APP_MERCADO_PUBLICO_BASE_URL}/Portal/Modules/Menu/Menu.aspx`, '_top')
      return
    }
    location.href = `${REACT_APP_MERCADO_PUBLICO_BASE_URL}/Home`
  }

  const handleClickBack = (isPrivated, userType) => {
    if (isPrivated && userType === USER_TYPE.PROVIDER) {
      deleteCookie('access_token')
      window.open(`${REACT_APP_MERCADO_PUBLICO_BASE_URL}/Portal/Modules/Menu/Menu.aspx`, '_top')
      return
    }
    location.href = `${REACT_APP_BASE_URL}/busqueda`
  }

  const isInIframe = useMemo(() => {
    return inIframe()
  }, [])

  useEffectOnce(() => {
    if (isInIframe) {
      window.parent.postMessage(
        {
          action: 'scrollTo',
          value: {
            x: 0,
            y: 0
          }
        },
        REACT_APP_MERCADO_PUBLICO_BASE_URL
      )
    } else {
      window.scrollTo(0, 0)
    }
  })

  useEffect(() => {
    if (userType === '') return
    initializeReCaptcha({ authUserType: userType })

    const executeAll = async () => {
      const token = await executeReCaptcha({ action: 'publicInfo' })
      setToken(token)
    }
    if (userType === USER_TYPE.PUBLIC) {
      executeAll()
    }
  }, [userType])

  const handleBack = (isPrivated, userType) => () => handleClickBack(isPrivated, userType)

  return (
    <AuthorizationProviderContainer>
      <AuthorizationContext.Consumer>
        {({ isPrivated, userType }) => {
          return (
            <BrandLayout
              featured={<HeadingSectionContainer breadcrumbs={breadcrumbs(isPrivated, userType)} title={title} />}
              header={<HeaderContainer />}
            >
              <Row container margin="0" padding="0">
                {isPrivated && REACT_APP_FEATURE_FLAG_MESSAGE_INFO === 'true' && (
                  <Toast margin="18px 0 0" severity="warning">
                    Por un problema originado en la fuente oficial,{' '}
                    <strong>
                      no se encuentra disponible en el Registro de Proveedores la información actualizada sobre deudas
                      previsionales y de salud
                    </strong>
                    . Se recomienda solicitar a sus proveedores una Declaración Jurada y otros antecedentes que
                    considere pertinentes. Puede utilizar el formato disponible en nuestro&nbsp;
                    <Link
                      textDecoration
                      href="https://ayuda.mercadopublico.cl/material/?c=3df31e42-9b52-ee11-be6f-000d3a88f7bc"
                      rel="noreferrer"
                      target="_blank"
                    >
                      Centro de Ayuda.
                    </Link>
                  </Toast>
                )}
                <Row item xs={12} sm={12} md={9} padding="0">
                  <ProtectByFeatures
                    rule={features =>
                      features['com.seller/private.unique.seller'] || features['com.seller/public.unique.seller']
                    }
                  >
                    <PurchaseOrderDialog />
                  </ProtectByFeatures>
                  {!isPrivated ? (
                    <>
                      {REACT_APP_FEATURE_FLAG_MESSAGE_INFO === 'true' && (
                        <Toast margin="18px 0 0" severity="warning">
                          Por un problema originado en la fuente oficial,{' '}
                          <strong>
                            no se encuentra disponible en el Registro de Proveedores la información actualizada sobre
                            deudas previsionales y de salud
                          </strong>
                          . Se recomienda solicitar a sus proveedores una Declaración Jurada y otros antecedentes que
                          considere pertinentes. Puede utilizar el formato disponible en nuestro&nbsp;
                          <Link
                            textDecoration
                            href="https://ayuda.mercadopublico.cl/material/?c=3df31e42-9b52-ee11-be6f-000d3a88f7bc"
                            rel="noreferrer"
                            target="_blank"
                          >
                            Centro de Ayuda.
                          </Link>
                        </Toast>
                      )}
                      <BasicSellerInformationSectionContainer options={optionTabsPublic} />
                      <PublicSection>
                        <SellerTabWrapper prefix="seller" options={optionTabsPublic} />
                      </PublicSection>
                    </>
                  ) : (
                    <>
                      <BasicSellerInformationSectionContainer options={optionTabs} />
                      <PrivateSection>
                        <SellerTabWrapper prefix="seller" options={optionTabs} />
                      </PrivateSection>
                    </>
                  )}
                </Row>
                <Row item xs={12} sm={12} md={3} padding="0">
                  {!isPrivated ? (
                    <PublicSection>
                      <ProtectByFeatures rule={features => features['com.seller/summary.section.public']}>
                        <SummarySectionContainer />
                        <ProtectByFeatures rule={features => features['com.seller/ability.certificate.public']}>
                          <EnrollmentCertificateButton />
                        </ProtectByFeatures>
                        <ProtectByFeatures rule={features => features['com.seller/button.seller.pdf.private']}>
                          <FilePDFButton token={token} userType={userType} />
                        </ProtectByFeatures>
                      </ProtectByFeatures>
                    </PublicSection>
                  ) : (
                    <PrivateSection>
                      <SummarySectionContainer />
                      <ProtectByFeatures rule={features => features['com.seller/ability.certificate.private']}>
                        <EnrollmentCertificateButton />
                      </ProtectByFeatures>
                      <ProtectByFeatures rule={features => features['com.seller/button.seller.pdf.private']}>
                        <FilePDFButton userType={userType} />
                      </ProtectByFeatures>
                      <ProtectByFeatures rule={features => features['com.seller/button.affidavit.private']}>
                        <RedirectAffidavitButton />
                      </ProtectByFeatures>
                    </PrivateSection>
                  )}
                </Row>
              </Row>

              <Button color="default" variant="outlined" onClick={handleBack(isPrivated, userType)}>
                {isPrivated && userType === USER_TYPE.PROVIDER ? 'Volver al escritorio' : 'Volver'}
              </Button>
            </BrandLayout>
          )
        }}
      </AuthorizationContext.Consumer>
    </AuthorizationProviderContainer>
  )
}
export default SellerCard
